import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { VoraLogo } from "@/components/brand/VoraLogo";

/**
 * Production Terms of Service (v1.0).
 *
 * Routing, layout, and styling are unchanged — body content only.
 * The "[Insert Email]" string is intentionally left as a placeholder
 * until a contact address is finalized.
 */
export default function Terms() {
  return (
    <div className="vora-legal-page" data-testid="terms-page">
      <header className="vora-legal-header">
        <Link to="/" className="flex items-center gap-2" data-testid="terms-logo-link">
          <VoraLogo variant="icon" size={22} />
          <span className="text-base font-bold font-heading tracking-tight" style={{ color: "#111111" }}>VORA</span>
        </Link>
        <Link
          to="/signup"
          className="text-sm font-medium flex items-center gap-1.5"
          style={{ color: "#5F6368" }}
          data-testid="terms-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </header>

      <main className="vora-legal-main">
        <div className="vora-legal-card">
          <h1 className="vora-legal-title" data-testid="terms-title">Terms of Service</h1>
          <p className="vora-legal-meta" data-testid="terms-updated">
            Last Updated: April 30, 2026 · Version v1.0
          </p>

          <section className="vora-legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using VORA (the &ldquo;Platform&rdquo;), you agree to be bound by
              these Terms of Service (&ldquo;Terms&rdquo;).
            </p>
            <p>If you do not agree, you must not use the Platform.</p>
          </section>

          <section className="vora-legal-section">
            <h2>2. Eligibility</h2>
            <p>You must be at least 18 years old to use the Platform.</p>
            <p>By using VORA, you represent and warrant that you meet this requirement.</p>
          </section>

          <section className="vora-legal-section">
            <h2>3. Nature of the Platform</h2>
            <p>VORA is a software platform that provides:</p>
            <ul>
              <li>AI-generated nutrition insights</li>
              <li>Meal planning tools</li>
              <li>Food and recipe suggestions</li>
              <li>Integration with third-party services</li>
            </ul>
            <p>VORA is not a healthcare provider.</p>
          </section>

          <section className="vora-legal-section">
            <h2>4. No Medical Advice</h2>
            <p>The Platform does not provide:</p>
            <ul>
              <li>medical advice</li>
              <li>diagnosis</li>
              <li>treatment</li>
            </ul>
            <p>All information provided is for informational purposes only.</p>
            <p>
              You must consult a qualified healthcare provider before making decisions
              related to your health.
            </p>
          </section>

          <section className="vora-legal-section">
            <h2>5. AI-Generated Content</h2>
            <p>You acknowledge that:</p>
            <ul>
              <li>Content is generated by automated systems</li>
              <li>Outputs may be inaccurate, incomplete, or unsuitable</li>
              <li>Outputs are based only on the information you provide</li>
            </ul>
            <p>You agree to independently verify all information before relying on it.</p>
          </section>

          <section className="vora-legal-section">
            <h2>6. Assumption of Risk</h2>
            <p>By using the Platform, you acknowledge that:</p>
            <ul>
              <li>You are voluntarily participating in nutrition and wellness activities</li>
              <li>You assume full responsibility for your decisions</li>
              <li>Dietary changes may involve risks, including allergic reactions</li>
            </ul>
            <p>You use the Platform at your own risk.</p>
          </section>

          <section className="vora-legal-section">
            <h2>7. No Guarantees</h2>
            <p>VORA does not guarantee:</p>
            <ul>
              <li>weight loss</li>
              <li>health outcomes</li>
              <li>fitness results</li>
            </ul>
            <p>Results vary based on individual factors.</p>
          </section>

          <section className="vora-legal-section">
            <h2>8. Third-Party Services</h2>
            <p>
              The Platform may integrate with third-party services (e.g., grocery delivery,
              meal providers).
            </p>
            <p>VORA is not responsible for:</p>
            <ul>
              <li>product quality</li>
              <li>food safety</li>
              <li>delivery services</li>
              <li>third-party accuracy</li>
            </ul>
            <p>Your use of third-party services is at your own risk.</p>
          </section>

          <section className="vora-legal-section">
            <h2>9. User Responsibilities</h2>
            <p>You agree to:</p>
            <ul>
              <li>Provide accurate information</li>
              <li>Ensure your dietary choices are safe for you</li>
              <li>Not rely solely on the Platform for health decisions</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>10. Subscriptions &amp; Billing</h2>
            <p>VORA may offer paid subscriptions.</p>
            <ul>
              <li>Subscriptions renew automatically unless cancelled</li>
              <li>You are responsible for cancelling before renewal</li>
              <li>All payments are final and non-refundable except where required by law</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>11. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <p>VORA is not liable for any indirect, incidental, or consequential damages.</p>
            <p>VORA&rsquo;s total liability is limited to the lesser of:</p>
            <ul>
              <li>$100 CAD</li>
              <li>The amount you paid in the last 12 months</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>12. Indemnification</h2>
            <p>You agree to indemnify and hold harmless VORA from any claims arising from:</p>
            <ul>
              <li>Your use of the Platform</li>
              <li>Your health decisions</li>
              <li>Your violation of these Terms</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>13. Changes to the Platform</h2>
            <p>We may modify or discontinue the Platform at any time without notice.</p>
          </section>

          <section className="vora-legal-section">
            <h2>14. Termination</h2>
            <p>We may suspend or terminate your access at our discretion.</p>
          </section>

          <section className="vora-legal-section">
            <h2>15. Governing Law</h2>
            <p>These Terms are governed by the laws of Ontario, Canada.</p>
          </section>

          <section className="vora-legal-section">
            <h2>16. Contact</h2>
            <p>For questions, contact: [Insert Email]</p>
          </section>

          <div className="vora-legal-footer">
            <Link to="/signup" className="vora-legal-back-btn" data-testid="terms-footer-signup">
              Back to sign-up
            </Link>
            <Link to="/login" className="vora-legal-back-btn vora-legal-back-btn--ghost" data-testid="terms-footer-login">
              Back to log-in
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
